import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { FocusIcon } from '@atlaskit/logo';
import { Box } from '@atlaskit/primitives/compiled';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';

import { ProductKeys } from '../../../../../../common/constants/products';
import type { SearchResultProps } from '../../../../../../common/types';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchPassionfruitAsk_AtlassianSearchPage$key } from './__generated__/searchPassionfruitAsk_AtlassianSearchPage.graphql';
import { SearchPassionfruitAskStatusTargetDate } from './search-passionfruit-ask-status-target-date';

export const SearchPassionfruitAsk: ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
}: SearchResultProps<searchPassionfruitAsk_AtlassianSearchPage$key>) => React.JSX.Element = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
}: SearchResultProps<searchPassionfruitAsk_AtlassianSearchPage$key>) => {
	const data = useFragment(
		graphql`
			fragment searchPassionfruitAsk_AtlassianSearchPage on SearchResult {
				...searchPassionfruitAskStatusTargetDate_AtlassianSearchPage
				__typename
				id
				title
				type
				url
				... on SearchResultSpfAsk {
					lastModifiedDate
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type]);

	const onResultClicked = useFireResultClicked({ metadata, position });
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({ metadata, position });
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const title = useMemo(() => enrichExcerpt(data.title), [data.title]);
	const headerMetadataParts = [
		[
			'type',
			<Box>
				<SearchResultType type={data.type as SearchClientResultType} />
			</Box>,
		],
		[
			'last-modified',
			data.lastModifiedDate ? (
				<Box>
					<LastModified lastModified={data.lastModifiedDate} />
				</Box>
			) : null,
		],
	];

	return (
		<SearchResult
			{...data}
			product={ProductKeys.Focus}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={data.url}
			title={title}
			icon={<FocusIcon size="medium" shouldUseNewLogoDesign />}
			headerMetadataParts={headerMetadataParts}
			description={<SearchPassionfruitAskStatusTargetDate node={data} />}
			cloudId={cloudId}
			position={position}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};

export const isSearchPassionfruitAsk: (
	node?: any,
) => node is searchPassionfruitAsk_AtlassianSearchPage$key = (
	node?: any,
): node is searchPassionfruitAsk_AtlassianSearchPage$key => {
	return node.__typename === 'SearchResultSpfAsk';
};
